/**
 * Initiative tracker store (Zustand).
 *
 * Mirrors the canonical server `InitiativeState` shape. Wired to socket
 * events `initiative_updated` and `initiative_cleared` from useWebSocket.
 *
 * The empty default `{ entries: [], currentIndex: 0, round: 1 }` is the
 * same shape `session_state.initiative` always carries (per iter-4 of the
 * initiative spec) so the panel can render without any null checks.
 */

import { create } from 'zustand';

export interface InitiativeEntry {
  tokenId: string;
  name: string;
  value: number;
}

interface InitiativeStore {
  /** The map this initiative belongs to. Empty string means "no active initiative". */
  mapId: string;
  entries: InitiativeEntry[];
  currentIndex: number;
  round: number;
  /** Last initiative-related server error, surfaced in the panel. Null when clear. */
  error: string | null;

  /** Replace the whole state from a server `initiative_updated` payload or session_state. */
  setInitiative: (state: {
    mapId: string;
    entries: InitiativeEntry[];
    currentIndex: number;
    round: number;
  }) => void;

  /** Reset to the empty default in response to a server `initiative_cleared`. */
  clearInitiative: () => void;

  /** Record an initiative-related server `error` event so the GM sees it. */
  setError: (message: string | null) => void;
}

export const useInitiativeStore = create<InitiativeStore>((set) => ({
  mapId: '',
  entries: [],
  currentIndex: 0,
  round: 1,
  error: null,

  // A successful update clears any stale error.
  setInitiative: ({ mapId, entries, currentIndex, round }) =>
    set({ mapId, entries, currentIndex, round, error: null }),

  clearInitiative: () =>
    set({ mapId: '', entries: [], currentIndex: 0, round: 1, error: null }),

  setError: (message) => set({ error: message }),
}));
